import { cn } from '@/lib/utils'
import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { FC, Fragment } from 'react'

import { Button } from '@droidmap/frontend-ui'

interface ModalProps {
  title?: string
  description?: React.ReactNode
  icon?: React.ReactNode
  headerRight?: React.ReactNode
  isOpen: boolean
  onClose: () => void
  children?: React.ReactNode
  /** Max width class (default: sm:max-w-6xl) */
  maxWidth?: string
  /** Hide the close (X) button (default: false) */
  hideCloseButton?: boolean
  /** Tailwind classes for the icon wrapper background. Default: bg-accent/10. */
  iconWrapperClassName?: string
  /** Tailwind classes for the icon glyph color. Default: text-accent. */
  iconClassName?: string
}

const Modal: FC<ModalProps> = ({
  title,
  description,
  icon,
  headerRight,
  isOpen,
  onClose,
  children,
  maxWidth = 'sm:max-w-6xl',
  hideCloseButton = false,
  iconWrapperClassName,
  iconClassName,
}) => {
  if (!isOpen) {
    return null
  }

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-40 text-text" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={`relative w-full transform overflow-hidden rounded-lg border border-border bg-panel px-5 pb-5 pt-6 text-left shadow-elevated transition-all sm:my-8 ${maxWidth} sm:p-7`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {icon && (
                      <div
                        className={cn(
                          'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl',
                          iconWrapperClassName ?? 'bg-accent/10',
                        )}
                      >
                        <div
                          className={cn(
                            'h-5 w-5',
                            iconClassName ?? 'text-accent',
                          )}
                        >
                          {icon}
                        </div>
                      </div>
                    )}
                    <div className="min-h-10 min-w-0 flex flex-col justify-center">
                      {title && (
                        <DialogTitle
                          as="h3"
                          className="text-base font-semibold leading-tight"
                        >
                          {title}
                        </DialogTitle>
                      )}
                      {description && (
                        <Description className="text-sm text-text-dim mt-0.5">
                          {description}
                        </Description>
                      )}
                    </div>
                  </div>
                  {(headerRight || !hideCloseButton) && (
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {headerRight}
                      {!hideCloseButton && (
                        <Button
                          size={'icon'}
                          variant={'outline'}
                          onClick={onClose}
                        >
                          <span className="sr-only">Close</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="pt-5 space-y-4">{children}</div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default Modal
